/**
 * EmailAIDispatchDialog — Preview + confirmación del "Distribuir email al evento"
 *
 * Flujo:
 *   1. Usuario pulsa "🪄 Distribuir al evento" en un email del EmailHub
 *   2. Backend analiza (extrae novios/fecha/links YouTube + contexto + PDFs)
 *   3. Este dialog muestra:
 *      - Evento detectado o dropdown para elegir entre candidatos
 *      - Lista de peticiones a crear (editables: título/artista/momento)
 *      - Lista de documentos a subir
 *   4. Usuario confirma → POST /api/emails/{id}/apply-analysis
 */

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Sparkles, FileText, Youtube, Music, AlertCircle, Check, Search, X } from 'lucide-react';
import { API_URL } from '../config';
import { ScheduleNotesReviewSections, ExtraMomentsReviewSection, ReunionDetectedCard, buildOverwriteWarning } from './EventScheduleNotes';

export const EmailAIDispatchDialog = ({ isOpen, onClose, email, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [selectedEventoId, setSelectedEventoId] = useState('');
  const [peticiones, setPeticiones] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [error, setError] = useState(null);
  // Debug modal: muestra lo que el backend "leyó" de cada PDF
  const [showPdfDebug, setShowPdfDebug] = useState(false);
  // (2026-10) Horarios por momento + observaciones operativas detectados
  const [horarios, setHorarios] = useState([]);
  const [selectedHorarios, setSelectedHorarios] = useState(new Set());
  const [observaciones, setObservaciones] = useState([]);
  const [selectedObservaciones, setSelectedObservaciones] = useState(new Set());
  const [selectedEventoHorarios, setSelectedEventoHorarios] = useState([]);
  // Fase 3 (2026-10): momentos extra + reunión previa detectada
  const [momentosExtra, setMomentosExtra] = useState([]);
  const [selectedMomentosExtra, setSelectedMomentosExtra] = useState(new Set());
  const [reunion, setReunion] = useState({ detectada: false });
  const [reunionEnabled, setReunionEnabled] = useState(false);
  const [reunionFecha, setReunionFecha] = useState('');
  const [reunionHora, setReunionHora] = useState('');

  // Momentos canónicos (2026-11-01): el campo momento SIEMPRE debe ser uno
  // de estos 4 — el detalle específico detectado por la IA (ej. "entrada
  // novia") se guarda en la Nota, que es la referencia real para el DJ.
  const momentosPredefinidos = ['', 'Ceremonia', 'Cóctel', 'Banquete', 'Baile'];

  // Fetch analysis al abrir
  useEffect(() => {
    if (!isOpen || !email?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAnalysis(null);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.post(
          `${API_URL}/emails/${email.id}/analyze-for-event`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 },
        );
        if (cancelled) return;
        setAnalysis(data);
        setPeticiones(data.peticiones || []);
        setDocumentos(data._documentos_full || []);
        const h = data.horarios_detectados || [];
        const o = data.observaciones_detectadas || [];
        setHorarios(h);
        setSelectedHorarios(new Set(h.map((_, idx) => idx)));
        setObservaciones(o);
        setSelectedObservaciones(new Set(o.map((_, idx) => idx)));
        const me = data.momentos_extra_detectados || [];
        setMomentosExtra(me);
        setSelectedMomentosExtra(new Set(me.map((_, idx) => idx)));
        const r = data.reunion_detectada || { detectada: false };
        setReunion(r);
        setReunionEnabled(!!r.detectada);
        setReunionFecha(r.fecha || '');
        setReunionHora(r.hora || '');
        // Autoseleccionar SOLO si hay match fuerte (score ≥0.85). Si no, dejar
        // el dropdown sin seleccionar para forzar confirmación consciente.
        if (data.evento_match?.id) {
          setSelectedEventoId(data.evento_match.id);
        } else {
          setSelectedEventoId('');
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e.response?.data?.detail || e.message;
          setError(`Error analizando: ${msg}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, email?.id]);

  // (2026-10) Al elegir/cambiar el evento destino, trae sus horarios actuales
  // (solo si hay horarios detectados que podrían entrar en conflicto).
  useEffect(() => {
    if (!selectedEventoId || horarios.length === 0) {
      setSelectedEventoHorarios([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/playlists/${selectedEventoId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSelectedEventoHorarios(data.horarios_momentos || []);
      } catch (_e) {
        if (!cancelled) setSelectedEventoHorarios([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEventoId, horarios.length]);

  const toggleHorario = (idx) => {
    setSelectedHorarios((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const toggleObservacion = (idx) => {
    setSelectedObservaciones((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const toggleMomentoExtra = (idx) => {
    setSelectedMomentosExtra((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const updatePeticion = (idx, field, value) => {
    setPeticiones((arr) => {
      const next = [...arr];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };
  const removePeticion = (idx) => {
    setPeticiones((arr) => arr.filter((_, i) => i !== idx));
  };
  const removeDocumento = (idx) => {
    setDocumentos((arr) => arr.filter((_, i) => i !== idx));
  };

  const canApply = useMemo(() => {
    return selectedEventoId && (
      peticiones.length > 0 || documentos.length > 0 ||
      selectedHorarios.size > 0 || selectedObservaciones.size > 0 ||
      selectedMomentosExtra.size > 0 || (reunionEnabled && reunion.detectada)
    );
  }, [selectedEventoId, peticiones, documentos, selectedHorarios, selectedObservaciones, selectedMomentosExtra, reunionEnabled, reunion]);

  const handleApply = async () => {
    if (!canApply) return;
    const horariosPayload = horarios.filter((_, idx) => selectedHorarios.has(idx));
    const observacionesPayload = observaciones.filter((_, idx) => selectedObservaciones.has(idx));
    const momentosExtraPayload = momentosExtra.filter((_, idx) => selectedMomentosExtra.has(idx));
    const wantsReunionTask = reunionEnabled && reunion.detectada;
    if (wantsReunionTask && !reunionFecha) {
      toast.warning('Introduce la fecha de la reunión para crear la tarea');
      return;
    }
    if (horariosPayload.length > 0 || momentosExtraPayload.length > 0) {
      const conflicts = buildOverwriteWarning(
        [...horariosPayload, ...momentosExtraPayload.map(m => ({ momento: m.nombre, hora_inicio: m.hora_inicio, hora_fin: m.hora_fin, es_extra: true }))],
        selectedEventoHorarios
      );
      if (conflicts.length > 0) {
        const ok = window.confirm(
          `Vas a sobrescribir estos horarios ya guardados en el evento:\n\n${conflicts.join('\n')}\n\n¿Confirmas?`
        );
        if (!ok) return;
      }
    }
    setApplying(true);
    try {
      const token = localStorage.getItem('token');
      // Stripe del campo content_base64 interno de documentos
      const docsPayload = documentos.map((d) => ({
        filename: d.filename,
        size: d.size,
        content_type: d.content_type,
        content_base64: d.content_base64,
      }));
      const { data } = await axios.post(
        `${API_URL}/emails/${email.id}/apply-analysis`,
        {
          evento_id: selectedEventoId,
          peticiones,
          documentos: docsPayload,
          horarios: horariosPayload,
          observaciones: observacionesPayload,
          momentos_extra: momentosExtraPayload,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 },
      );
      // Tarea de reunión previa (Fase 3) — endpoint independiente
      let reunionCreada = false;
      if (wantsReunionTask) {
        try {
          const resR = await axios.post(
            `${API_URL}/playlists/${selectedEventoId}/crear-tarea-reunion`,
            { fecha: reunionFecha, hora: reunionHora, texto: reunion.texto || '' },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          reunionCreada = !!resR.data?.created;
        } catch (eR) {
          toast.error(`Error creando tarea de reunión: ${eR.response?.data?.detail || eR.message}`);
        }
      }
      if (data.success) {
        // Desglose por origen para feedback visual rico
        const fromPdf = peticiones.filter(p => p.fuente === 'pdf').length;
        const fromEmail = peticiones.filter(p => p.fuente === 'email' || !p.fuente).length;
        const nEventoNombre = analysis?.evento_match?.nombre_novios || 'el evento';
        const parts = [];
        if (data.peticiones_creadas > 0) {
          const origenStr = [
            fromPdf > 0 ? `📄 ${fromPdf} del PDF` : null,
            fromEmail > 0 ? `📧 ${fromEmail} del email` : null,
          ].filter(Boolean).join(', ');
          parts.push(`🎵 ${data.peticiones_creadas} petición(es)${origenStr ? ` (${origenStr})` : ''}`);
        }
        if (data.documentos_subidos > 0) {
          parts.push(`📎 ${data.documentos_subidos} documento(s) adjunt${data.documentos_subidos === 1 ? 'o' : 'os'}`);
        }
        if (data.horarios_aplicados > 0) {
          parts.push(`🕐 ${data.horarios_aplicados} horario(s)`);
        }
        if (data.observaciones_aplicadas > 0) {
          parts.push(`📝 ${data.observaciones_aplicadas} observación(es)`);
        }
        if (data.youtube_downloads_queued > 0) {
          parts.push(`⏳ ${data.youtube_downloads_queued} MP3 descargándose en 2º plano`);
        }
        if (reunionCreada) {
          parts.push('📅 tarea de reunión creada');
        }
        toast.success(
          `Aplicado a ${nEventoNombre}`,
          {
            description: parts.join('  •  ') || 'Sin cambios',
            duration: 7000,
          }
        );
      } else {
        toast.warning(
          `Aplicado con errores: ${data.peticiones_creadas} peticiones, ${data.documentos_subidos} docs, ${data.errores?.length || 0} errores`,
          {
            description: (data.errores || []).slice(0, 2).join(' · '),
            duration: 8000,
          }
        );
      }
      if (onApplied) onApplied(data);
      onClose();
    } catch (e) {
      toast.error(`Error aplicando: ${e.response?.data?.detail || e.message}`);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !applying) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] bg-slate-900 text-white border-slate-700 overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Distribuir email al evento
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-amber-400" />
            <p className="text-slate-300">La IA está analizando el email...</p>
            <p className="text-xs text-slate-500">Extrae novios, fecha, links YouTube y texto de PDFs</p>
          </div>
        )}

        {error && !loading && (
          <div className="flex items-center gap-2 bg-red-900/30 border border-red-700 rounded-lg p-4" data-testid="ai-dispatch-error">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        {analysis && !loading && !error && (
          <div className="flex-1 overflow-y-auto space-y-4 pr-2" data-testid="ai-dispatch-preview">
            {/* Extraído por IA */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm">
              <p className="text-xs text-slate-400 mb-1">🤖 Extraído por IA:</p>
              <div className="flex flex-wrap gap-2">
                {analysis.nombres_novios_extraidos && (
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                    👰 {analysis.nombres_novios_extraidos}
                  </span>
                )}
                {analysis.fecha_extraida && (
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs">
                    📅 {analysis.fecha_extraida}
                  </span>
                )}
                {analysis.tipo_evento_extraido && (
                  <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded text-xs">
                    🎉 {analysis.tipo_evento_extraido}
                  </span>
                )}
                {analysis.pdfs_procesados > 0 && (
                  <span className="px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded text-xs">
                    📄 {analysis.pdfs_procesados} documento(s) analizado(s)
                  </span>
                )}
                {analysis.pdf_debug_previews?.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowPdfDebug(true)}
                    className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-xs flex items-center gap-1 transition-colors"
                    title="Ver el texto que el agente extrajo del documento (PDF/DOCX)"
                    data-testid="pdf-debug-preview-btn"
                  >
                    <Search className="w-3 h-3" />
                    Ver qué leyó del documento
                  </button>
                )}
              </div>
              {analysis.doc_legacy_warnings?.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="doc-legacy-warnings">
                  {analysis.doc_legacy_warnings.map((w, i) => (
                    <div key={i} className="px-3 py-2 bg-amber-900/30 border border-amber-700/50 rounded text-xs text-amber-300">
                      {w}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selector de evento */}
            <div>
              <Label className="text-sm font-semibold text-white mb-1 block">
                Evento destino
                {analysis.evento_match?.id && (
                  <span className="ml-2 text-xs text-green-400">
                    ✓ Match detectado (score {analysis.evento_match.score})
                  </span>
                )}
              </Label>
              <select
                value={selectedEventoId}
                onChange={(e) => setSelectedEventoId(e.target.value)}
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm"
                data-testid="ai-dispatch-event-select"
              >
                <option value="">-- Elige un evento --</option>
                {analysis.candidatos?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.score > 0 ? '⭐ ' : ''}
                    {c.nombre_novios} — {c.fecha || 'sin fecha'}
                    {c.score > 0 ? ` (score ${c.score})` : ''}
                  </option>
                ))}
              </select>
              {(!analysis.candidatos || analysis.candidatos.length === 0) && (
                <p className="text-xs text-amber-400 mt-1">
                  ⚠ No se encontraron eventos próximos. Crea el evento primero en la pestaña Eventos.
                </p>
              )}
            </div>

            {/* Peticiones */}
            {peticiones.length > 0 && (
              <div>
                <Label className="text-sm font-semibold text-white mb-2 block flex items-center gap-2 flex-wrap">
                  <Music className="w-4 h-4" />
                  Peticiones a crear ({peticiones.length})
                  {peticiones.filter(p => p.fuente === 'pdf').length > 0 && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-700/50"
                      title="Peticiones extraídas del PDF adjunto (guión ceremonia)"
                    >
                      📄 {peticiones.filter(p => p.fuente === 'pdf').length} del PDF
                    </span>
                  )}
                  {peticiones.filter(p => p.fuente === 'email' || !p.fuente).length > 0 && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-700/50"
                      title="Peticiones extraídas del cuerpo del email"
                    >
                      📧 {peticiones.filter(p => p.fuente === 'email' || !p.fuente).length} del email
                    </span>
                  )}
                </Label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {peticiones.map((p, idx) => (
                    <div key={idx} className="bg-slate-800/50 border border-slate-700 rounded p-2 space-y-2" data-testid={`ai-dispatch-peticion-${idx}`}>
                      <div className="flex gap-2 items-center">
                        {p.youtube_url && <Youtube className="w-4 h-4 text-red-400 shrink-0" />}
                        {p.spotify_url && <Music className="w-4 h-4 text-green-400 shrink-0" />}
                        {p.fuente === 'pdf' && (
                          <span
                            className="text-[10px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-700/50 shrink-0"
                            title="Extraída del PDF adjunto"
                          >
                            📄 PDF
                          </span>
                        )}
                        <Input
                          value={p.titulo}
                          onChange={(e) => updatePeticion(idx, 'titulo', e.target.value)}
                          placeholder="Título"
                          className="bg-slate-900 border-slate-600 text-xs flex-1 h-8"
                        />
                        <Input
                          value={p.artista}
                          onChange={(e) => updatePeticion(idx, 'artista', e.target.value)}
                          placeholder="Artista"
                          className="bg-slate-900 border-slate-600 text-xs flex-1 h-8"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removePeticion(idx)}
                          className="text-red-400 hover:bg-red-900/30 h-8 px-2"
                          data-testid={`ai-dispatch-peticion-remove-${idx}`}
                        >
                          ✕
                        </Button>
                      </div>
                      <div className="flex gap-2 items-center">
                        <select
                          value={p.momento || ''}
                          onChange={(e) => updatePeticion(idx, 'momento', e.target.value)}
                          className="bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs"
                          data-testid={`ai-dispatch-peticion-momento-${idx}`}
                        >
                          {momentosPredefinidos.map((m) => (
                            <option key={m} value={m}>{m || '(sin momento)'}</option>
                          ))}
                        </select>
                        <Input
                          value={p.nota || ''}
                          onChange={(e) => updatePeticion(idx, 'nota', e.target.value)}
                          placeholder="Nota (ej: Entrada novia, Corte de tarta...)"
                          className="bg-slate-900 border-slate-600 text-xs flex-1 h-8"
                          data-testid={`ai-dispatch-peticion-nota-${idx}`}
                        />
                        <a
                          href={p.youtube_url || p.spotify_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-cyan-400 hover:underline truncate max-w-[200px] shrink-0"
                        >
                          {(p.youtube_url || p.spotify_url || '').substring(0, 40)}...
                        </a>
                      </div>
                      {p.contexto && (
                        <p className="text-xs text-slate-500 italic truncate" title={p.contexto}>
                          💬 {p.contexto.substring(0, 80)}...
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documentos */}
            {documentos.length > 0 && (
              <div>
                <Label className="text-sm font-semibold text-white mb-2 block flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Documentos a subir ({documentos.length})
                </Label>
                <div className="space-y-1">
                  {documentos.map((d, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-slate-800/50 border border-slate-700 rounded px-2 py-1.5 text-xs" data-testid={`ai-dispatch-doc-${idx}`}>
                      <FileText className="w-4 h-4 text-blue-400" />
                      <span className="flex-1 truncate">{d.filename}</span>
                      <span className="text-slate-500">{Math.round(d.size / 1024)} KB</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDocumento(idx)}
                        className="text-red-400 hover:bg-red-900/30 h-7 px-2"
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {peticiones.length === 0 && documentos.length === 0 && horarios.length === 0 && observaciones.length === 0 &&
              momentosExtra.length === 0 && !reunion.detectada && (
              <div className="text-center text-slate-500 py-6 text-sm">
                No se detectaron peticiones, documentos, horarios ni observaciones en este email.
              </div>
            )}

            {/* Horarios y observaciones detectados (2026-10, Fase 1) + momentos extra/reunión (Fase 3) */}
            {(horarios.length > 0 || observaciones.length > 0 || momentosExtra.length > 0 || reunion.detectada) && (
              <div className="border-t border-slate-700 pt-3 space-y-3">
                <ScheduleNotesReviewSections
                  horarios={horarios}
                  selectedHorarios={selectedHorarios}
                  onToggleHorario={toggleHorario}
                  observaciones={observaciones}
                  selectedObservaciones={selectedObservaciones}
                  onToggleObservacion={toggleObservacion}
                />
                <ExtraMomentsReviewSection
                  momentosExtra={momentosExtra}
                  selectedExtra={selectedMomentosExtra}
                  onToggleExtra={toggleMomentoExtra}
                />
                <ReunionDetectedCard
                  reunion={reunion}
                  enabled={reunionEnabled}
                  onToggleEnabled={setReunionEnabled}
                  fecha={reunionFecha}
                  hora={reunionHora}
                  onChangeFecha={setReunionFecha}
                  onChangeHora={setReunionHora}
                />
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="shrink-0 flex justify-end gap-2 pt-3 border-t border-slate-700 safe-bottom">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-slate-600 text-white hover:bg-slate-700"
            disabled={applying}
            data-testid="ai-dispatch-cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleApply}
            disabled={!canApply || applying || loading}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="ai-dispatch-apply-btn"
            title={
              !selectedEventoId
                ? 'Elige primero un evento destino en el dropdown'
                : (peticiones.length === 0 && documentos.length === 0 && selectedHorarios.size === 0 && selectedObservaciones.size === 0)
                  ? 'Este email no tiene nada seleccionado para distribuir'
                  : 'Aplicar al evento seleccionado'
            }
          >
            {applying ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Aplicando...</>
            ) : (
              <><Check className="w-4 h-4 mr-1" /> Aplicar al evento</>
            )}
          </Button>
        </div>
      </DialogContent>

      {/* Modal debug: muestra texto reconstruido por el dispatcher para cada PDF */}
      <Dialog open={showPdfDebug} onOpenChange={setShowPdfDebug}>
        <DialogContent
          className="bg-slate-900 text-white border-slate-700 max-w-4xl w-[95vw] h-[85vh] p-0 overflow-hidden flex flex-col"
          data-testid="pdf-debug-modal"
        >
          <DialogHeader className="shrink-0 px-4 py-3 border-b border-slate-700 flex-row items-center justify-between space-y-0">
            <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Search className="w-5 h-5 text-cyan-400 shrink-0" />
              <span>Extracción del PDF — debug</span>
            </DialogTitle>
            <button
              onClick={() => setShowPdfDebug(false)}
              className="p-2 rounded hover:bg-slate-800 text-slate-300"
              title="Cerrar"
              data-testid="pdf-debug-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </DialogHeader>
          <div className="flex-1 overflow-auto p-4 space-y-4 bg-slate-950">
            <p className="text-xs text-slate-400">
              Este es el texto que el dispatcher reconstruyó de cada PDF (incluye URLs de
              hipervínculos clickeables detectados en su posición original). Si algún link
              que esperabas NO aparece aquí, pypdf no lo pudo leer — posible PDF escaneado
              o firmado digitalmente.
            </p>
            {(analysis?.pdf_debug_previews || []).map((p, idx) => (
              <div key={idx} className="border border-slate-700 rounded-lg overflow-hidden">
                <div className="bg-slate-800 px-3 py-2 flex items-center gap-2 text-xs">
                  <FileText className="w-4 h-4 text-red-400 shrink-0" />
                  <span className="font-mono truncate flex-1">{p.filename}</span>
                  <span className="text-slate-400 shrink-0">{p.char_count} chars</span>
                </div>
                <pre className="p-3 text-xs text-slate-200 whitespace-pre-wrap break-words font-mono max-h-[50vh] overflow-auto">
                  {p.text || '(sin texto extraíble)'}
                </pre>
              </div>
            ))}
            {(!analysis?.pdf_debug_previews || analysis.pdf_debug_previews.length === 0) && (
              <div className="text-slate-400 text-sm italic">
                No hay PDFs adjuntos en este email.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default EmailAIDispatchDialog;
